/*
 * HVACloud AI · Register aus Doku ableiten — Task modal.
 *
 * Operator lädt ein Vendor-Handbuch (PDF) oder eine Register-Tabelle
 * (CSV/TXT/MD) hoch. Server ruft Anthropic, liefert eine Liste von
 * Modbus-Registern (h:/i:/c:/d:) mit optionaler Baumstruktur zurück. Der
 * Operator sieht die Vorschau, gibt Name + Beschreibung + Sichtbarkeit
 * ein, klickt "Als Template speichern" — die Content-Snapshot landet
 * per POST /api/schema-templates als neues Schema-Template.
 */

import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import { AiTaskChrome, type AiTaskStage } from './AiTaskChrome';
import { api, ApiError } from '../../api/client';
import { HeroRegisterExtract } from '../../icons/HvacIcons';
import { BRAND } from '../../brand/brand';

interface Props {
  onClose(): void;
  onSaved?: (templateId: string) => void;
}

type ProtocolHint = 'auto' | 'modbus-rtu' | 'modbus-tcp';

interface ParsedNode {
  symbol: string;
  displayName: string;
  parentSymbol: string | null;
}

interface ParsedLeaf {
  address: string;
  symbol: string;
  displayName: string;
  valueType: string;
  unit: string | null;
  scale: number | null;
  decimals: number | null;
  writable: boolean;
  nodeSymbol: string | null;
}

interface ParseResponse {
  detectedProtocol: string;
  nodes: ParsedNode[];
  leaves: ParsedLeaf[];
  rationale: string | null;
}

interface CreateTemplateResponse {
  id: string;
  version: number;
}

const PROGRESS_MESSAGES = [
  `Dokument wird an ${BRAND.aiName} übergeben…`,
  'Register-Adressen werden extrahiert…',
  'Werte-Typen + Einheiten werden zugeordnet…',
  'Optionale Gruppierung wird geprüft…',
  'Fast fertig — Vorschlag wird zusammengestellt…',
];

const HINT_PLACEHOLDER =
  'z.B. "Nur Blöcke Heizkreis 1-3 übernehmen" oder "Adressen im Dokument sind Modicon-4x — bitte auf h:N mappen"';

const MAX_UPLOAD_MB = 20;

const ACCEPTED_EXTENSIONS = '.pdf,.csv,.txt,.md';

export function AiDocumentationParserModal({ onClose, onSaved }: Props) {
  const [hint, setHint] = useState('');
  const [stage, setStage] = useState<AiTaskStage>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [protocolHint, setProtocolHint] = useState<ProtocolHint>('auto');
  const [proposal, setProposal] = useState<ParseResponse | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const busy = stage === 'loading' || stage === 'applying' || saving;

  const onFileChosen = useCallback((f: File | null) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setErrorMessage(`Datei ist zu groß (max ${MAX_UPLOAD_MB} MB).`);
      return;
    }
    setErrorMessage(null);
    setFile(f);
  }, []);

  const openFilePicker = () => fileInputRef.current?.click();

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0] ?? null;
    onFileChosen(f);
  };

  const run = async () => {
    if (!file) {
      setErrorMessage('Bitte zuerst eine Datei auswählen.');
      return;
    }
    setStage('loading');
    setErrorMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('protocolHint', protocolHint);
      if (hint.trim()) form.append('operatorHint', hint.trim());
      const res = await api.postForm<ParseResponse>('/api/discovery/parse-documentation', form);
      setProposal(res);
      // Prefill template name from file basename minus extension.
      if (!templateName.trim()) {
        const base = file.name.replace(/\.[^.]+$/, '').trim();
        if (base.length > 0) setTemplateName(base.slice(0, 200));
      }
      setStage('result');
    } catch (e) {
      const msg = e instanceof ApiError
        ? (typeof e.body === 'object' && e.body !== null && 'error' in e.body
            ? String((e.body as { error: unknown }).error)
            : `KI-Aufruf fehlgeschlagen (HTTP ${e.status})`)
        : e instanceof Error ? e.message : 'Unbekannter Fehler';
      setErrorMessage(msg);
      setStage('error');
    }
  };

  const saveAsTemplate = async () => {
    if (!proposal) return;
    const name = templateName.trim();
    if (name.length === 0) {
      setErrorMessage('Template-Name ist erforderlich.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      const bodyJson = buildTemplateBodyJson(proposal, name);
      const protocol = proposal.detectedProtocol.startsWith('modbus') ? proposal.detectedProtocol : 'modbus-tcp';
      const res = await api.post<CreateTemplateResponse>('/api/schema-templates', {
        name,
        description: templateDescription.trim() || undefined,
        protocol,
        scope: 0, // Tenant — sicherer Default
        bodyJson,
      });
      onSaved?.(res.id);
      onClose();
    } catch (e) {
      const msg = e instanceof ApiError
        ? (typeof e.body === 'object' && e.body !== null && 'error' in e.body
            ? String((e.body as { error: unknown }).error)
            : `Speichern fehlgeschlagen (HTTP ${e.status})`)
        : e instanceof Error ? e.message : 'Unbekannter Fehler';
      setErrorMessage(msg);
      setSaving(false);
    }
  };

  const rows = useMemo(() => proposal ? buildTreeRows(proposal) : [], [proposal]);

  return (
    <AiTaskChrome
      title={`${BRAND.aiName} · Register aus Doku ableiten`}
      description="Vendor-Handbuch als PDF, CSV oder TXT hochladen — die KI extrahiert Modbus-Register und schlägt eine optionale Gruppierung vor. Das Ergebnis wird als Schema-Template gespeichert."
      hint={hint}
      onHintChange={setHint}
      hintPlaceholder={HINT_PLACEHOLDER}
      stage={stage}
      errorMessage={errorMessage}
      progressMessages={PROGRESS_MESSAGES}
      onClose={onClose}
      hero={
        <div style={{ color: 'var(--ai-accent, var(--accent))' }}>
          <HeroRegisterExtract size={80} />
        </div>
      }
      footer={
        stage === 'result' && proposal ? (
          <>
            <div class="hv-modal__footer-spacer" />
            <button class="hv-btn" onClick={onClose} disabled={busy}>Abbrechen</button>
            <button
              class="hv-btn hv-btn--primary"
              onClick={saveAsTemplate}
              disabled={busy || templateName.trim().length === 0}
            >
              {saving ? 'Speichere…' : 'Als Template speichern'}
            </button>
          </>
        ) : (
          <>
            <div class="hv-modal__footer-spacer" />
            <button class="hv-btn" onClick={onClose} disabled={busy}>Abbrechen</button>
            <button
              class="hv-btn hv-btn--primary"
              onClick={run}
              disabled={busy || !file}
            >
              {stage === 'loading' ? `${BRAND.aiName} arbeitet…` : 'Register ableiten'}
            </button>
          </>
        )
      }
      idleExtra={
        <>
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Datei</label>
            <div
              onClick={openFilePicker}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              style={{
                border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border-subtle)'}`,
                borderRadius: 'var(--radius-1)',
                padding: '18px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragOver ? 'color-mix(in srgb, var(--accent) 6%, transparent)' : 'transparent',
                transition: 'border-color .12s, background .12s',
              }}
            >
              {file ? (
                <div>
                  <strong>{file.name}</strong>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: 2 }}>
                    {(file.size / 1024).toFixed(0)} KB · <a onClick={(e) => { e.stopPropagation(); setFile(null); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Andere Datei wählen</a>
                  </div>
                </div>
              ) : (
                <div style={{ color: 'var(--text-secondary)' }}>
                  <div>PDF / CSV / TXT / MD hier ablegen oder <strong>klicken zum Auswählen</strong></div>
                  <div style={{ fontSize: 'var(--fs-xs)', marginTop: 4 }}>Excel? Bitte als CSV exportieren. Max {MAX_UPLOAD_MB} MB.</div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                style={{ display: 'none' }}
                onChange={(e) => onFileChosen((e.currentTarget as HTMLInputElement).files?.[0] ?? null)}
              />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Protokoll</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(['auto', 'modbus-tcp', 'modbus-rtu'] as const).map((p) => (
                <label key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', border: `1px solid ${protocolHint === p ? 'var(--accent)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-1)', cursor: 'pointer', background: protocolHint === p ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'transparent', fontSize: 'var(--fs-sm)' }}>
                  <input type="radio" name="protocol-hint" checked={protocolHint === p} onChange={() => setProtocolHint(p)} style={{ margin: 0 }} />
                  <span>{p === 'auto' ? 'Auto-Erkennung' : p}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      }
    >
      {stage === 'result' && proposal ? (
        <ResultView
          proposal={proposal}
          rows={rows}
          templateName={templateName}
          templateDescription={templateDescription}
          onNameChange={setTemplateName}
          onDescriptionChange={setTemplateDescription}
        />
      ) : null}
    </AiTaskChrome>
  );
}

function ResultView({
  proposal, rows, templateName, templateDescription, onNameChange, onDescriptionChange,
}: {
  proposal: ParseResponse;
  rows: TreeRow[];
  templateName: string;
  templateDescription: string;
  onNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
}) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 'var(--fs-sm)' }}>
        <span><strong>{proposal.leaves.length}</strong> Register</span>
        <span><strong>{proposal.nodes.length}</strong> Gruppen</span>
        <span>Protokoll: <code>{proposal.detectedProtocol}</code></span>
      </div>
      {proposal.rationale && (
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: 0 }}>{proposal.rationale}</p>
      )}

      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-1)', maxHeight: 320, overflow: 'auto', margin: '8px 0' }}>
        {rows.length === 0 ? (
          <div style={{ padding: 12, color: 'var(--text-secondary)' }}>Keine Gruppen — alle Register sitzen auf Wurzelebene.</div>
        ) : rows.map((r) => (
          <div key={r.kind === 'node' ? `n:${r.node.symbol}` : `l:${r.leaf.address}`} style={{ padding: '4px 8px', paddingLeft: 8 + r.depth * 16, fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--border-subtle)' }}>
            {r.kind === 'node' ? (
              <strong>{r.node.displayName}</strong>
            ) : (
              <span>
                <code style={{ background: 'var(--surface-elevated)', padding: '0 4px', borderRadius: 2, marginRight: 6 }}>{r.leaf.address}</code>
                {r.leaf.displayName}
                <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>
                  · {r.leaf.valueType}
                  {r.leaf.unit ? ` · ${r.leaf.unit}` : ''}
                  {r.leaf.scale != null ? ` · ×${r.leaf.scale}` : ''}
                  {r.leaf.writable ? ' · rw' : ''}
                </span>
              </span>
            )}
          </div>
        ))}
        {proposal.leaves.filter((l) => l.nodeSymbol == null).length > 0 && (
          <div style={{ padding: '4px 8px', color: 'var(--text-secondary)', fontSize: 'var(--fs-xs)' }}>
            {proposal.leaves.filter((l) => l.nodeSymbol == null).length} Register ohne Gruppe (Wurzelebene)
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={{ display: 'block', fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Template-Name *</label>
        <input
          type="text"
          value={templateName}
          maxLength={200}
          onInput={(e) => onNameChange((e.currentTarget as HTMLInputElement).value)}
          style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-1)', background: 'var(--surface-panel)', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', boxSizing: 'border-box' }}
          placeholder="z.B. Vaillant aroTHERM Modbus"
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={{ display: 'block', fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Beschreibung</label>
        <textarea
          value={templateDescription}
          maxLength={2000}
          rows={2}
          onInput={(e) => onDescriptionChange((e.currentTarget as HTMLTextAreaElement).value)}
          style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-1)', background: 'var(--surface-panel)', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', boxSizing: 'border-box', resize: 'vertical' }}
          placeholder="optional — Quelle, Version, Besonderheiten"
        />
      </div>
    </div>
  );
}

// ---- helpers -----------------------------------------------------------

type TreeRow =
  | { kind: 'node'; node: ParsedNode; depth: number }
  | { kind: 'leaf'; leaf: ParsedLeaf; depth: number };

function buildTreeRows(p: ParseResponse): TreeRow[] {
  const bySym = new Map(p.nodes.map((n) => [n.symbol, n]));
  const childrenOf = new Map<string | null, ParsedNode[]>();
  for (const n of p.nodes) {
    const parent = n.parentSymbol && bySym.has(n.parentSymbol) ? n.parentSymbol : null;
    const arr = childrenOf.get(parent) ?? [];
    arr.push(n);
    childrenOf.set(parent, arr);
  }
  const leavesUnder = new Map<string, ParsedLeaf[]>();
  for (const l of p.leaves) {
    if (!l.nodeSymbol) continue;
    if (!bySym.has(l.nodeSymbol)) continue;
    const arr = leavesUnder.get(l.nodeSymbol) ?? [];
    arr.push(l);
    leavesUnder.set(l.nodeSymbol, arr);
  }
  const out: TreeRow[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const n of childrenOf.get(parent) ?? []) {
      out.push({ kind: 'node', node: n, depth });
      for (const l of leavesUnder.get(n.symbol) ?? []) {
        out.push({ kind: 'leaf', leaf: l, depth: depth + 1 });
      }
      walk(n.symbol, depth + 1);
    }
  };
  walk(null, 0);
  // Leaves ohne nodeSymbol landen als Wurzel-Blätter.
  for (const l of p.leaves) {
    if (!l.nodeSymbol || !bySym.has(l.nodeSymbol)) {
      out.push({ kind: 'leaf', leaf: l, depth: 0 });
    }
  }
  return out;
}

function buildTemplateBodyJson(p: ParseResponse, deviceName: string): string {
  // Enum-Werte: siehe HVACloud.Model.Enums
  // ValueTypeKind: Bool=0, Int=1, Float=2, String=3, Enum=4, Flags=5
  // Writability: ReadOnly=0, WriteOnly=1, ReadWrite=2
  // NodeOrigin: Real=0, Virtual=1
  // MetricKind: Unknown=0, Instantaneous=1
  // Sensitivity: Normal=0
  // AlarmRole: None=0
  const vt: Record<string, number> = { Bool: 0, Int: 1, Float: 2, String: 3, Enum: 4, Flags: 5 };

  // Snapshot expects nodes with a snapshot-local clientId + parentClientId
  // that reference each other (not database Guids). We use the symbol
  // itself as the local id — stable + human-readable.
  const nodesOut = p.nodes.map((n, i) => ({
    clientId: n.symbol,
    parentClientId: n.parentSymbol ?? null,
    symbol: n.symbol,
    displayName: n.displayName,
    origin: 1, // Virtual — vom KI abgeleitet
    category: null,
    typicalDefinitionId: null,
    typicalDefinitionVersion: null,
    overridesJson: null,
    attributesJson: null,
    sortOrder: i,
  }));

  // Falls Leaves ohne nodeSymbol vorkommen brauchen wir einen Wurzel-Container.
  const rootLeaves = p.leaves.filter((l) => !l.nodeSymbol || !p.nodes.find((n) => n.symbol === l.nodeSymbol));
  let rootSymbol: string | null = null;
  if (rootLeaves.length > 0) {
    rootSymbol = '__root';
    nodesOut.unshift({
      clientId: rootSymbol,
      parentClientId: null,
      symbol: 'allgemein',
      displayName: 'Allgemein',
      origin: 1,
      category: null,
      typicalDefinitionId: null,
      typicalDefinitionVersion: null,
      overridesJson: null,
      attributesJson: null,
      sortOrder: -1,
    });
  }

  const leavesOut = p.leaves.map((l, i) => ({
    parentNodeClientId: l.nodeSymbol && p.nodes.find((n) => n.symbol === l.nodeSymbol) ? l.nodeSymbol : rootSymbol!,
    symbol: l.symbol,
    displayName: l.displayName,
    valueType: vt[l.valueType] ?? 1,
    metricKind: 1, // Instantaneous
    writability: l.writable ? 2 : 0,
    readSensitivity: 0,
    writeSensitivity: 0,
    safetyMarker: false,
    escalateToAlarm: false,
    alarmRole: 0,
    forceAllowed: false,
    unit: l.unit,
    dimension: null,
    minValue: null,
    maxValue: null,
    decimals: l.decimals ?? null,
    maxLength: null,
    enumEntriesJson: null,
    flagBitsJson: null,
    warnLow: null,
    warnHigh: null,
    alarmLow: null,
    alarmHigh: null,
    address: l.address,
    scale: l.scale ?? null,
    offset: null,
    pollIntervalSeconds: null,
    heartbeatSeconds: null,
    deadband: null,
    attributesJson: null,
    sortOrder: i,
  }));

  const symbol = deviceName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'device';

  const snapshot = {
    device: {
      symbol,
      name: deviceName,
      protocol: p.detectedProtocol.startsWith('modbus') ? p.detectedProtocol : 'modbus-tcp',
      commandCapable: leavesOut.some((l) => l.writability === 2),
      forceCapable: false,
    },
    nodes: nodesOut,
    leaves: leavesOut,
  };
  return JSON.stringify(snapshot);
}
